import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

import { ImageCropperPopupComponent } from '@teem/image-cropper/image-cropper-popup.component';

import { url as UrlRegexPattern } from '@teem/constants/regex-patterns';
import { SharedDataService } from '@teem/services/shared-data.service';
import { LoaderService } from '@teem/services/loader.service';
import { messages as i18n } from '@teem/constants/messages';
import { country as CountryApi } from '@teem/constants/api';
import { UserService } from '@teem/services/user.service';
import { DEFAULT_LOGO } from '@teem/constants/defaults';
import * as COUNTRIES from '@teem/constants/country';
import { fileNameFromUrl } from '@teem/helpers/functions';


@Component({
  selector: 'account-setting',
  templateUrl: './account-setting.component.html',
  styleUrls: ['./account-setting.component.css']
})
export class AccountSettingComponent implements OnInit {
  @ViewChild('logoFile') myPond: any;

  accountSettingForm: FormGroup;
  userDetails: any;
  isSubmitted = false;
  showChangePasswordBlock = false;

  logoFile: any = null;
  logoUrl: any = null;
  defaultLogoUrl: string = DEFAULT_LOGO;

  // Ref: https://pqina.nl/filepond/docs/patterns/api/server/
  uploadToServer = {
    process: (fieldName, file, metadata, load, error, progress, abort) => {
      let oldFileName = this.logoUrl ? fileNameFromUrl(this.logoUrl) : null;
      let timer;
      if (oldFileName != file.name) {
        // Create data object containing the file and the file metadata
        const formData = new FormData();
        formData.append('logo_image', file, file.name);
        formData.append(fieldName, JSON.stringify(metadata));

        this.userService.uploadFile(formData).subscribe(
          (data) => {
            let user = this.sharedDataService.getItem('userInfo');
            user.has_logo = true;
            user.logo = (<any>data).body?.logo;

            this.sharedDataService.setItem('userInfo', user);
          },
          (err) => { console.error(`File Upload Error: ${err.message}`); },
        );
        // Progress indicator supported, set progress to 25% of 1
        progress(true, 0.25, 1);
        // Fake completion after 1 second
        timer = setTimeout(() => {
          load(file); // or { body: 'unique-file-id' }
        }, 1000);
      } else {
        load(this.logoUrl);
        progress(false);
      }


      // Should expose an abort method so the request can be cancelled by the user
      return {
        abort: () => {
          // abort your request here (we clear timer for demo purposes)
          clearTimeout(timer);

          // updates FilePond interface
          abort();
        }
      };
    },

    revert: (uniqueFileId, load, error) => {
      // remove file from server here

      this.userService.removeFile().subscribe(
        (data) => {
          console.log(data);
          let user = this.sharedDataService.getItem('userInfo');
          user.has_logo = false;
          this.sharedDataService.setItem('userInfo', user);
          load();
        },
        (err) => { console.error(`Remove File Upload Error: ${err.message}`); },
      );
      console.log("deleted  ", uniqueFileId);
    }
  };

  pondOptions = {
    multiple: false,
    // allowRevert: false,
    allowReplace: false,
    allowRemove: true,
    allowImageResize: true,
    allowImagePreview: true,
    imageResizeMode: 'cover',
    imagePreviewHeight: 170,
    imagePreviewMinHeight: 170,
    imagePreviewMaxHeight: 170,
    imageResizeTargetWidth: 170,
    imageResizeTargetHeight: 170,
    server: this.uploadToServer,
    acceptedFileTypes: 'image/png, image/jpeg, image/gif',
    labelIdle: `<span class="icon-upload"></span>`,
    beforeRemoveFile: this.pondHandleRemoveFile,
  };

  @Input('userDetails')
  set getInfo(val: any) {
    this.userDetails = val;
    this.accountSettingForm.patchValue({
      first_name: val.first_name,
      last_name: val.last_name,
      email: val.email,
      phone: val.phone,
      business_name: val.business_name,
      color_palette: val.color_palette ? val.color_palette : '#ffffff',
      dial_code: val.dial_code,
    });

    this.logoUrl = this.userDetails.logo && this.userDetails.logo != this.defaultLogoUrl ? this.userDetails.logo : null;
  }

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private loaderService: LoaderService,
    private sharedDataService: SharedDataService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer
  ) {
    this.createAccountFormGroup();
  }

  ngOnInit(): void {
    this.formChange()
    this.sharedDataService.asObservable().subscribe((data) => {
      if (data) {
        if (data.userInfo) {
          if (data.userInfo.dial_code) {
            this.accountSettingForm.patchValue({
              dial_code: data.userInfo.dial_code ? data.userInfo.dial_code : this.userDetails.dial_code,
            })
          }
        }
      }
    })
  }

  pondHandleInit() {
    // console.log('FilePond has initialised', this.myPond);
  }

  pondHandleAddFile(event: any) {
    // console.log('A file was added', event);

  }

  async pondHandleRemoveFile(event: any) {
    let confirm = await Swal.fire({
      title: i18n.are_you_sure,
      text: i18n.setting.you_want_to_delete_logo,
      icon: 'info',
      showCloseButton: true,
      showCancelButton: true,
      focusConfirm: false,
      confirmButtonText: i18n.yes,
      cancelButtonText: i18n.no_cancel_it
    })
    if (confirm.isConfirmed) {
      return true
    }
    return false;
  }

  createAccountFormGroup() {
    this.accountSettingForm = this.formBuilder.group({
      first_name: ['', [Validators.required, Validators.maxLength(99)]],
      last_name: ['', [Validators.required, Validators.maxLength(99)]],
      email: ['', [Validators.required, Validators.email]],
      business_name: [null, [Validators.required]],
      color_palette: [null, [Validators.required]],
      phone: ['', [Validators.required, Validators.pattern("^[0-9]*$"), Validators.minLength(5), Validators.maxLength(30)]],
      dial_code: ['',],
      old_password: ['', Validators.minLength(6)],
      new_password: ['', Validators.minLength(6)],
      confirm_password: ['', Validators.minLength(6)]
    },
      {
        validators: this.confirmMatch.bind(this)
      });
  }

  confirmMatch(formGroup: FormGroup) {
    const { value: password } = formGroup.get('new_password');
    const { value: confirmPassword } = formGroup.get('confirm_password');
    const { value: oldPassword } = formGroup.get('old_password');
    if (oldPassword != '' && password == '' && confirmPassword == '') {
      return { bothPasswordRequired: true };
    }
    return password === confirmPassword ? null : { passwordNotMatch: true };
  }

  get f() {
    return this.accountSettingForm.controls;
  }

  formChange() {
    this.accountSettingForm.get('new_password').valueChanges.subscribe(
      value => {
        if (value != '') {
          this.accountSettingForm.get('old_password').setValidators([Validators.required, Validators.minLength(6)]);
        } else {
          this.accountSettingForm.get('old_password').clearValidators();
        }

        this.accountSettingForm.get('old_password').updateValueAndValidity();
      });
  }

  onSubmit() {
    this.isSubmitted = true;

    if (this.accountSettingForm.valid) {
      this.loaderService.startLoading();

      let fd = this.accountSettingForm.value;

      this.userService.updateAccountInfo(fd).subscribe(
        (res: any) => {
          this.loaderService.stopLoading();
          this.accountSettingForm.patchValue({
            old_password: null,
            new_password: null,
            confirm_password: null
          });

          let user = this.sharedDataService.getItem('userInfo');
          user.first_name = fd.first_name;
          user.first_name = fd.last_name;
          user.full_name = fd.first_name + " " + fd.last_name;
          user.has_logo = res.data.has_logo;
          user.logo = res.data.logo;
          user.preference = res.data.preference;

          this.sharedDataService.setItem('userInfo', user);

          this.accountSettingForm.get('old_password').clearValidators();
          this.accountSettingForm.patchValue({
            phone: res.data.phone,
          });
          this.accountSettingForm.get('old_password').updateValueAndValidity();
          Object.assign(this.userDetails, this.accountSettingForm.value);
          Swal.fire(i18n.done, i18n.setting.general_information_updated, 'success');
          this.showChangePasswordBlock = false;
        },
        (err) => {
          this.loaderService.stopLoading();
          if (err.status === 422) {
            if (err.error && err.error.errors && Object.keys(err.error.errors).length) {
              let validationErrors = err.error.errors;
              Object.keys(validationErrors).forEach((prop) => {
                this.accountSettingForm.get(prop)?.setErrors({
                  serverError: validationErrors[prop]
                })
              })
            }
          }

          Swal.fire(i18n.alert.error, i18n.getError(err), 'error');
        }
      )
      this.isSubmitted = false;
    }
  }

  public onColorChange(color: any) {
    console.log(color);
    this.accountSettingForm.patchValue({
      color_palette: color.toHexString()
    });
  }


  get colorPalette() {
    return this.accountSettingForm.get('color_palette');
    // f.color_palette.value
  }

  updateColorPalette(value: string) {
    if (value != this.userDetails.color_palette) {
      this.loaderService.startLoading();
      this.userService.updateBusinessInfo({ 'color_palette': value }).subscribe(
        (res: any) => {
          this.loaderService.stopLoading();
          let user = this.sharedDataService.getItem('userInfo');
          user.preference.text_color = res.data.preference.text_color;
          this.sharedDataService.setItem('userInfo', user);
          Swal.fire(i18n.done, i18n.setting.color_palette_updated, 'success');
        });
    }
  }

  onLogoChange(event: any, fileInput): void {
    const modalRef = this.modalService.open(ImageCropperPopupComponent, { backdrop: 'static', backdropClass: 'black-transparent' });
    // Set id on modal dialog
    Object.assign(modalRef.componentInstance.cropperConfig, { aspectRatio: 1 / 1, minWidth: 175, resizeToWidth: 175 });
    modalRef.componentInstance.croppedImage = event;
    modalRef.componentInstance.el.nativeElement.parentNode.parentNode.parentNode.id = "imageCropper";
    modalRef.result.then(result => {
      // console.log(result);
      this.logoFile = result;
      this.logoUrl = result === null ? null : this.sanitizer.bypassSecurityTrustResourceUrl(result);
      fileInput.value = null;
    }, fallbackResult => {
      this.logoUrl = null;
      fileInput.value = null;
      // do nothing
    })
  }
}
